import React, { Suspense, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EventsMapPage, resolveEventsPageView } from '../experimental/events-map';
import { EventCard } from '../../components/events/PublicCards';
import EventLikeButton from './EventLikeButton';
import EventsMasonryGrid from './EventsMasonryGrid';
import { MASONRY_MIN_COL_WIDTH } from './eventsMasonry';
import './PublicEventsSection.css';
import './EventHubPage.css';
import PublicCalendar from '../../components/events/PublicCalendar';
import CategoryToggleGroup from '../../components/CategoryToggleGroup/CategoryToggleGroup';
import { useAuth } from '../../contexts/AuthContext';
import {
  EXPERIMENT_EVENTS_MAP,
  isExperimentEnabled,
} from '../../services/users/experiments';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import { useDebouncedValue } from '../../utils/useDebouncedValue';
import {
  groupEventsByCalendarDay,
  getEventCalendarDay,
  isSameCalendarDay,
  EVENT_LOAD_DAY_WINDOW,
  EVENT_SEO_LIST_DAYS,
} from '../../services/dateUtils';
import {
  EVENT_CATEGORY_DEFINITIONS,
  buildCategoryToggleOptions,
} from '../../services/eventCategories';
import { useEventCategoryRenames } from '../../utils/useEventCategoryRenames';
import { getEventPublicCategories } from '../../services/eventDisplayFields';
import {
  getBestEventsSectionLabel,
  isHomepageFeaturedEditorialPick,
  sortEventsByEditorialPick,
  sortFeaturedEventsByDate,
} from '../../services/eventEditorialPick';
import { buildEventsCollectionSchema, setJsonLdScript } from '../../services/seoSchema';
import {
  getFriendsGoingData,
} from '../../services/userProfile';
import { logEventSearch } from '../../services/eventSearchLogs';
import { logAnalyticsEvent } from '../../analytics/logAnalyticsEvent';
import { trackUxEvent } from '../../services/uxEvents';
import PublicLoadingFab from '../../components/PublicLoadingFab/PublicLoadingFab';
import { usePublicEventFeed } from './hooks/usePublicEventFeed';
import { subscribePublicEventPick } from '../../services/events/publicEventSaveTheDate';

const EventSingleModal = React.lazy(() => import('./EventSingleModal'));

const FEATURED_PRIORITY_POSTERS = 3;
const FEATURED_GRID_PROPS = {
  minColWidth: MASONRY_MIN_COL_WIDTH / 2,
  mobileColumns: 3,
  gapPx: 12,
};

const EVENTS_LOADING_PHRASES = [
  'Best Events Loading',
  'Asking Hamo',
  'Looping',
  'Copying MySpace.Com',
  'Loading Events',
  'Searching olx.ba',
  'Loading Events',
  'Loading All All Events',
];

/**
 * Self-contained upcoming events section for the home page.
 * Calendar / category / upcoming totals come from eventAggregates.
 * Cards load in day-complete 5-day windows via usePublicEventFeed.
 *
 * @param {{ onTotalCount?: (n: number, hasMore?: boolean) => void, collectionSchema?: boolean }} props
 */
const PublicEventsSection = ({
  onTotalCount,
  presetCategories = null,
  presetDates = null,
  showHeading = true,
  collectionSchema = true,
}) => {
  const { currentUser, userProfile } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { reportError } = useErrorReport();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapLabOn = isExperimentEnabled(userProfile, EXPERIMENT_EVENTS_MAP);
  const mapView = resolveEventsPageView(mapLabOn, searchParams) === 'map';

  const setEventsView = (nextView) => {
    const params = new URLSearchParams(searchParams);
    if (nextView === 'map') {
      params.set('view', 'map');
    } else {
      params.delete('view');
      params.delete('when');
    }
    setSearchParams(params, { replace: true });
  };
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const categoryRenameRevision = useEventCategoryRenames();

  // Intent segment presets replace the chip selection (null = leave as is).
  const presetKey = Array.isArray(presetCategories) ? presetCategories.join('|') : null;
  useEffect(() => {
    if (presetKey === null) return;
    setSelectedCategories(presetKey ? presetKey.split('|') : []);
  }, [presetKey]);
  const presetDatesKey = Array.isArray(presetDates)
    ? presetDates.map((day) => day?.toISOString?.() || '').join('|')
    : '';
  useEffect(() => {
    if (!presetDatesKey) return;
    setSelectedDates(presetDates);
  }, [presetDatesKey, presetDates]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarFilterSource, setCalendarFilterSource] = useState('public');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [friendInterestByEventId, setFriendInterestByEventId] = useState({});
  const [friendsEvents, setFriendsEvents] = useState([]);

  const {
    events,
    aggregate,
    countsReady,
    loading,
    loadingMore,
    hasMore,
    isDayGroupComplete,
    loadNextWindow,
    sentinelRef,
    eventsStillLoading,
  } = usePublicEventFeed({ selectedDates, initialWindowDays: EVENT_SEO_LIST_DAYS });

  const hasDayFilter = selectedDates.length > 0;
  const showMoreControl = hasMore && !hasDayFilter;
  const dayFilterPending = hasDayFilter
    && selectedDates.some((day) => !isDayGroupComplete(day))
    && loadingMore;

  const friendIdsKey = useMemo(
    () => JSON.stringify([...(userProfile?.friendIds || [])].filter(Boolean).sort()),
    [userProfile?.friendIds]
  );

  useEffect(() => {
    let cancelled = false;
    if (!currentUser?.uid || friendIdsKey === '[]') {
      setFriendInterestByEventId({});
      setFriendsEvents([]);
      return undefined;
    }

    getFriendsGoingData(JSON.parse(friendIdsKey), { hydrate: false })
      .then((data) => {
        if (cancelled) return;
        setFriendInterestByEventId(data.interestByEventId);
        setFriendsEvents(Array.isArray(data.events) ? data.events : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setFriendInterestByEventId({});
          setFriendsEvents([]);
          reportError(err, {
            pageLabel: 'Events',
            source: 'event-feed-friends',
            silent: true,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, friendIdsKey, reportError]);

  const handleOpenEvent = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  const handleCloseEvent = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  useEffect(() => subscribePublicEventPick(({ eventId, editorialPick }) => {
    setSelectedEvent((current) => (
      current?.id === eventId ? { ...current, editorialPick } : current
    ));
  }), []);
  const handleSelectDates = useCallback((dates, source = 'public') => {
    const next = Array.isArray(dates) ? dates.filter(Boolean) : [];
    setSelectedDates(next);
    setCalendarFilterSource(next.length ? source : 'public');
  }, []);

  const handleViewFriends = useCallback(() => {
    navigate('/friends');
  }, [navigate]);

  const handleUnlockCalendar = useCallback(() => {
    openAuthModal({ mode: 'signin' });
  }, [openAuthModal]);

  useEffect(() => {
    if (!onTotalCount) return;
    onTotalCount(aggregate.upcomingCount, false);
  }, [aggregate.upcomingCount, onTotalCount]);

  const searchFilteredEvents = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    if (!term) return events;
    return events.filter(event =>
      (event.title || '').toLowerCase().includes(term) ||
      (event.location || '').toLowerCase().includes(term) ||
      (event.description || '').toLowerCase().includes(term)
    );
  }, [events, debouncedSearch]);

  const lastLoggedSearchRef = useRef('');

  useEffect(() => {
    const term = debouncedSearch.trim();
    if (term.length < 2) {
      lastLoggedSearchRef.current = '';
      return undefined;
    }
    if (term.toLowerCase() === lastLoggedSearchRef.current) {
      return undefined;
    }
    lastLoggedSearchRef.current = term.toLowerCase();

    const resultCount = searchFilteredEvents.length;
    logAnalyticsEvent('event_search', {
      search_term: term.slice(0, 100),
      result_count: resultCount,
    });
    logEventSearch({
      query: term,
      resultCount,
      source: 'events',
      pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
      uid: currentUser?.uid || null,
      email: currentUser?.email || null,
    }).catch(() => {
      /* analytics must never break search */
    });
    return undefined;
  }, [debouncedSearch, searchFilteredEvents.length, currentUser?.uid, currentUser?.email]);

  const categoryFilteredEvents = useMemo(() => {
    if (selectedCategories.length === 0) return searchFilteredEvents;
    return searchFilteredEvents.filter((event) => {
      const categories = getEventPublicCategories(event);
      return selectedCategories.some((category) => categories.includes(category));
    });
  }, [searchFilteredEvents, selectedCategories]);

  const categoryOptions = useMemo(() => {
    // Revision bumps after category renames mutate the module label map.
    void categoryRenameRevision;
    return buildCategoryToggleOptions({
      definitions: EVENT_CATEGORY_DEFINITIONS,
      counts: aggregate.byCategory || {},
      includeZero: false,
    });
  }, [aggregate.byCategory, categoryRenameRevision]);

  const calendarFilteredEvents = useMemo(() => {
    if (selectedDates.length === 0) return categoryFilteredEvents;
    const byDay = categoryFilteredEvents.filter((event) =>
      selectedDates.some((day) => isSameCalendarDay(getEventCalendarDay(event), day))
    );
    if (calendarFilterSource === 'friends') {
      return byDay.filter((event) => Boolean(friendInterestByEventId[event.id]));
    }
    return byDay;
  }, [categoryFilteredEvents, selectedDates, calendarFilterSource, friendInterestByEventId]);

  useEffect(() => {
    if (!collectionSchema || loading || events.length === 0) {
      setJsonLdScript('m2b-events-schema', null);
      return undefined;
    }

    setJsonLdScript('m2b-events-schema', buildEventsCollectionSchema(events, 20));
    return () => setJsonLdScript('m2b-events-schema', null);
  }, [collectionSchema, events, loading]);

  const featuredEvents = useMemo(
    () =>
      sortFeaturedEventsByDate(
        calendarFilteredEvents.filter((event) =>
          isHomepageFeaturedEditorialPick(event.editorialPick, event)
        )
      ),
    [calendarFilteredEvents]
  );

  const bestEventsLabel = useMemo(() => getBestEventsSectionLabel(), []);

  const eventGroups = useMemo(() => {
    if (selectedDates.length > 0) {
      return groupEventsByCalendarDay(calendarFilteredEvents)
        .filter((group) => !group.day || isDayGroupComplete(group.day))
        .map((group) => ({
          ...group,
          events: sortEventsByEditorialPick(group.events),
        }));
    }

    const groups = groupEventsByCalendarDay(calendarFilteredEvents)
      .filter((group) => !group.day || isDayGroupComplete(group.day))
      .map((group) => ({
        ...group,
        events: sortEventsByEditorialPick(group.events),
      }));

    if (featuredEvents.length === 0) return groups;

    return [
      {
        day: null,
        label: bestEventsLabel,
        events: featuredEvents,
        featured: true,
      },
      ...groups,
    ];
  }, [
    calendarFilteredEvents,
    selectedDates,
    featuredEvents,
    bestEventsLabel,
    isDayGroupComplete,
  ]);

  return (
    <section id="events" aria-labelledby={showHeading ? 'events-section-heading' : undefined}>
      {showHeading ? (
        <h2 id="events-section-heading" className="pub-seo-context__heading">
          Best events Sarajevo
        </h2>
      ) : null}

      <CategoryToggleGroup
        className="pub-category-filters"
        options={categoryOptions}
        selected={selectedCategories}
        onChange={(next) => {
          setSelectedCategories(next);
          const category = (next || []).find((c) => !selectedCategories.includes(c));
          if (category) {
            trackUxEvent({ name: 'category_filter', surface: 'events', category });
          }
        }}
        label="Filter events by category"
        multiple={false}
        showCounts
        allCount={aggregate.upcomingCount}
        maxVisible={5}
      />

      <div className="pub-search-bar">
        <input
          type="text"
          className="pub-search-input"
          placeholder="Search by title, place or description…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <PublicCalendar
        panels="friendsAndPublic"
        events={events}
        publicDayCounts={aggregate.byDay}
        friendsEvents={friendsEvents}
        selectedDates={selectedDates}
        calendarFilterSource={calendarFilterSource}
        onSelectDates={handleSelectDates}
        personalLocked={!currentUser}
        loading={!countsReady}
        onUnlockPersonal={handleUnlockCalendar}
        onViewFriends={handleViewFriends}
      />

      {mapLabOn ? (
        <div className="pub-view-toggle events-map-switch" role="group" aria-label="Switch between list and map">
          <button
            type="button"
            className={`pub-view-toggle__btn${mapView ? '' : ' is-active'}`}
            onClick={() => setEventsView('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`pub-view-toggle__btn${mapView ? ' is-active' : ''}`}
            onClick={() => setEventsView('map')}
          >
            Map
          </button>
        </div>
      ) : null}

      {mapView ? (
        <Suspense fallback={<div className="pub-loading" role="status">Loading map…</div>}>
          <EventsMapPage
            onOpenEvent={handleOpenEvent}
            search={debouncedSearch}
            categories={selectedCategories}
            selectedDates={selectedDates}
            friendsOnly={calendarFilterSource === 'friends'}
            friendInterestByEventId={friendInterestByEventId}
            onClearDate={() => handleSelectDates([])}
          />
        </Suspense>
      ) : (
      <>
      {calendarFilteredEvents.length === 0 &&
      (search || hasDayFilter || selectedCategories.length > 0 || !hasMore) &&
      !loading &&
      !dayFilterPending ? (
        <div className="pub-empty">
          {hasDayFilter && !search && selectedCategories.length === 0
            ? 'No events on these days.'
            : search || hasDayFilter || selectedCategories.length > 0
              ? 'No events match your search, category, or selected day.'
              : 'No upcoming events yet.'}
        </div>
      ) : (
        <div className="pub-events-day-list">
          {eventGroups.map((group, groupIdx) => {
            const isLastGroup = groupIdx === eventGroups.length - 1;
            return (
              <div
                key={group.featured ? 'featured' : (group.day ? group.day.getTime() : 'unknown')}
                className={
                  group.featured
                    ? 'pub-events-day-group pub-events-day-group--featured'
                    : 'pub-events-day-group'
                }
              >
                <h3 className="pub-events-day-sep">{group.label}</h3>
                <EventsMasonryGrid
                  className="events-grid public-events-grid"
                  {...(group.featured ? FEATURED_GRID_PROPS : null)}
                >
                  {group.events.map((event, index) => (
                    <EventCard
                      key={group.featured ? `featured-${event.id}` : event.id}
                      event={event}
                      LikeControl={EventLikeButton}
                      onOpen={handleOpenEvent}
                      friendsGoing={friendInterestByEventId[event.id] || []}
                      imagePriority={Boolean(group.featured && index < FEATURED_PRIORITY_POSTERS)}
                    />
                  ))}
                  {isLastGroup && showMoreControl && (
                    <div
                      ref={sentinelRef}
                      className="event-pub-card event-pub-card--show-more"
                      onClick={loadNextWindow}
                      role="button"
                      tabIndex={0}
                      aria-label={`Show more ${EVENT_LOAD_DAY_WINDOW} days of events`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          loadNextWindow();
                        }
                      }}
                    >
                      <div className="event-pub-card__show-more-content">
                        <span className="event-pub-card__show-more-plus">++</span>
                        <span className="event-pub-card__show-more-title">
                          {loadingMore
                            ? 'LOADING…'
                            : `SHOW MORE ++ ${EVENT_LOAD_DAY_WINDOW} DAYS`}
                        </span>
                      </div>
                    </div>
                  )}
                </EventsMasonryGrid>
              </div>
            );
          })}
          {eventGroups.length === 0 && showMoreControl && (
            <div
              ref={sentinelRef}
              className="event-pub-card event-pub-card--show-more"
              onClick={loadNextWindow}
              role="button"
              tabIndex={0}
              aria-label={`Show more ${EVENT_LOAD_DAY_WINDOW} days of events`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  loadNextWindow();
                }
              }}
            >
              <div className="event-pub-card__show-more-content">
                <span className="event-pub-card__show-more-plus">++</span>
                <span className="event-pub-card__show-more-title">
                  {loadingMore
                    ? 'LOADING…'
                    : `SHOW MORE ++ ${EVENT_LOAD_DAY_WINDOW} DAYS`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {selectedEvent && (
        <Suspense fallback={null}>
          <EventSingleModal event={selectedEvent} onClose={handleCloseEvent} />
        </Suspense>
      )}

      {mapView ? null : (
        <PublicLoadingFab active={eventsStillLoading} phrases={EVENTS_LOADING_PHRASES} />
      )}

      {showHeading ? (
        <>
          <h1 className="pub-seo-context__heading">
            Events in Sarajevo
          </h1>
          <p className="pub-seo-context__copy">
            Concerts, festivals, and what is on in Sarajevo today and this week. Dates, venues, and links to each event.
          </p>
          <nav className="event-hub__nav" aria-label="Event guides">
            <Link to="/events">Events</Link>
            <Link to="/events/concerts">Concerts</Link>
            <Link to="/events/this-weekend">This weekend</Link>
            <Link to="/desavanja">Dešavanja</Link>
            <Link to="/events/week">This week</Link>
          </nav>
        </>
      ) : null}
    </section>
  );
};

export default PublicEventsSection;
